import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { EntityContent } from 'src/content/entity-content.js';
import '../../library/editors/index.js';
import '../../library/editors/components/d-edit-classification.js';
import '../../library/fields/index.js';
import '../../library/lists/d-list-section-attachment.js';
import '../../library/elements/d-action';
import type { SummaryView } from './summary-view';
import type { SummaryViewEditItem } from './summary-view-edit-item';
import type { UpdateSectionItem } from '../update-section-item';
import './monthly-hours/d-monthly-hours-summary';
import './time-balance/d-time-balance-summary';
import './absence/d-absence-summary';
import './sick-leave/d-sick-leave-summary';

/**
 * Summary view router component
 * Routes to specific summary components based on subType
 */
@customElement('d-summary-view')
export class DSummaryView extends EntityContent<SummaryView, SummaryViewEditItem> {
  static readonly styles = [...EntityContent.styles];
  @property({ type: Boolean })
  sortTimeBalanceSummaryByGroup = false;

  renderViewMode() {
    const payload = this.entityView.payload;

    switch (payload.subType) {
      case 'monthly-hours':
        return html` <d-monthly-hours-summary
          .data=${payload.data}
          .parentHref=${this.entityView.parentHref}
          .contentStickyTop=${this.contentStickyTop}
        ></d-monthly-hours-summary>`;

      case 'time-balance':
        return html` <d-time-balance-summary
          .data=${payload.data}
          .contentStickyTop=${this.contentStickyTop}
          .sortByGroup=${this.sortTimeBalanceSummaryByGroup}
        ></d-time-balance-summary>`;

      case 'absence':
        return html` <d-absence-summary
          .data=${payload.data}
          .contentStickyTop=${this.contentStickyTop}
        ></d-absence-summary>`;

      case 'sick-leave':
        return html` <d-sick-leave-summary
          .data=${payload.data}
          .contentStickyTop=${this.contentStickyTop}
        ></d-sick-leave-summary>`;

      case 'no-staffing-access':
        return html`
          <div style="padding: 20px;">
            <h2>${t('summaries.missingAccess')}</h2>
            <p>${payload.message}</p>
          </div>
        `;

      case 'not-found':
        return html`
          <div style="padding: 20px;">
            <h2>${t('summaries.summaryNotFound')}</h2>
            <p>${payload.message}</p>
          </div>
        `;

      default:
        return html`
          <div style="padding: 20px;">
            <h2>${t('summaries.unknownSummaryType')}</h2>
            <p>${t('summaries.summaryNotAvailable')}</p>
          </div>
        `;
    }
  }

  async initializeEditItem() {
    this.editItem = {
      name: this.entityView.name,
    };
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return undefined;
  }

  // Summaries are not editable
  protected hideEdit() {
    return true;
  }

  // Summaries cannot be deleted
  protected hideDelete(_mode: string, _preventDelete: boolean, _revision: any) {
    return true;
  }

  // Summaries cannot be added to collections
  renderCollections() {
    return html``;
  }

  // Summaries cannot have PDF
  protected hidePdf() {
    return true;
  }

  // Override header actions to only show "Lukk" button (no share, edit, etc.)
  mainHeaderActions() {
    if (this.entityView.payload.subType === 'time-balance') {
      return [
        {
          name: this.sortTimeBalanceSummaryByGroup ? t('summaries.hideGroups') : t('summaries.showGroups'),
          action: 'toggleSortByGroup',
        },
        {
          name: t('common:close'),
          href: this.parentHref || '#',
        },
      ];
    }
    return [
      {
        name: t('common:close'),
        href: this.parentHref || '#',
      },
    ];
  }

  // Override to hide all more actions (no PDF, delete, etc.)
  mainHeaderMoreActions() {
    return [];
  }

  protected handleAction(action: string) {
    if (action === 'toggleSortByGroup') {
      this.sortTimeBalanceSummaryByGroup = !this.sortTimeBalanceSummaryByGroup;
    }
  }

  // Use staffing icon for summaries
  protected getIcon() {
    return 'staffing';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-summary-view': DSummaryView;
  }
}
